import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * SplashScreen — Lunora's couture entry experience.
 *
 * Approved direction: full-bleed hero illustration matches the reference
 * exactly — gold mannequin couture insignia + "Lunora's" ribbon + "Bespoke.
 * Redefined." + champagne silk waves. Composition is BAKED into the image
 * (do NOT redesign).
 *
 * What we layer on top to make it alive (lightweight, GPU-only):
 *   · Slow breathing scale on the hero (1.00 ↔ 1.018, 6s)
 *   · 14 gold shimmer particles drifting upward at random offsets
 *   · Two soft warm radial blooms drifting in opposite directions (12s)
 *   · "Loading…" pulsing 3-dot indicator over the existing static label
 *   · 280ms crossfade from black to fully-revealed splash
 *
 * Mobile-first, navigates to /landing after 3s.
 */

const PARTICLES = [
  { left: '6%',  size: 3, delay: 0.0, duration: 12 },
  { left: '12%', size: 2, delay: 2.0, duration: 14 },
  { left: '18%', size: 4, delay: 0.6, duration: 11 },
  { left: '26%', size: 2, delay: 1.4, duration: 13 },
  { left: '34%', size: 3, delay: 0.2, duration: 10 },
  { left: '42%', size: 2, delay: 2.4, duration: 14 },
  { left: '50%', size: 4, delay: 1.0, duration: 12 },
  { left: '58%', size: 3, delay: 0.4, duration: 13 },
  { left: '66%', size: 2, delay: 1.8, duration: 11 },
  { left: '74%', size: 3, delay: 0.8, duration: 12 },
  { left: '82%', size: 4, delay: 2.2, duration: 14 },
  { left: '88%', size: 2, delay: 0.6, duration: 10 },
  { left: '94%', size: 3, delay: 1.6, duration: 13 },
  { left: '46%', size: 5, delay: 0.0, duration: 16 },
];

const SplashScreen = () => {
  const navigate = useNavigate();
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const t1 = setTimeout(() => setRevealed(true), 80);
    const t2 = setTimeout(() => navigate('/intro'), 3000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [navigate]);

  return (
    <div
      data-testid="splash-screen"
      className="fixed inset-0 overflow-hidden m-0 p-0"
      style={{ width: '100vw', height: '100dvh', background: 'linear-gradient(180deg, #F9E6C8 0%, #FBF0DC 50%, #FCF5E5 100%)' }}>

      {/* Hero brand mark — single unified logo across the app */}
      <img
        data-testid="splash-hero"
        src="/lunora_logo.png"
        alt="Lunora's — Bespoke. Redefined."
        className="absolute inset-0 block lunora-hero-breathe lunora-splash-img"
        style={{
          width: '100vw',
          height: '100dvh',
          objectPosition: 'center',
          opacity: revealed ? 1 : 0,
          transition: 'opacity 900ms ease-out',
          willChange: 'transform, opacity',
        }} />

      {/* Soft warm radial bloom — drifts slowly across to suggest depth */}
      <div
        data-testid="splash-bloom-1"
        className="absolute pointer-events-none lunora-bloom-1"
        style={{
          width: '70vw', height: '70vw',
          background: 'radial-gradient(circle, rgba(255, 220, 150, 0.20) 0%, rgba(255, 220, 150, 0) 65%)',
          top: '15%', left: '-10%',
          filter: 'blur(20px)',
          willChange: 'transform',
        }} />
      <div
        data-testid="splash-bloom-2"
        className="absolute pointer-events-none lunora-bloom-2"
        style={{
          width: '60vw', height: '60vw',
          background: 'radial-gradient(circle, rgba(245, 206, 140, 0.18) 0%, rgba(245, 206, 140, 0) 65%)',
          top: '40%', right: '-15%',
          filter: 'blur(20px)',
          willChange: 'transform',
        }} />

      {/* Gold shimmer particles — drift upward, randomized */}
      {PARTICLES.map((p, i) => (
        <span key={i}
          data-testid={`splash-particle-${i}`}
          className="absolute pointer-events-none lunora-particle"
          style={{
            left: p.left,
            bottom: '-12px',
            width: p.size, height: p.size,
            background: '#E5C46B',
            borderRadius: '50%',
            opacity: 0,
            boxShadow: `0 0 ${p.size * 4}px rgba(229, 196, 107, 0.85), 0 0 ${p.size * 2}px rgba(255, 240, 200, 0.6)`,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
            willChange: 'transform, opacity',
          }} />
      ))}

      {/* Pulsing 3-dot loading indicator (overlaid in lower-third where the
          baked-in "LOADING..." text sits — gives it gentle life) */}
      <div
        data-testid="splash-loading-dots"
        className="absolute left-1/2 -translate-x-1/2 flex items-center gap-1.5"
        style={{
          bottom: '17%',
          opacity: revealed ? 1 : 0,
          transition: 'opacity 700ms ease-out 600ms',
        }}>
        {[0, 1, 2].map(i => (
          <span key={i}
            className="block rounded-full lunora-dot"
            style={{
              width: 4, height: 4,
              background: '#C8A24A',
              boxShadow: '0 0 8px rgba(229, 196, 107, 0.9)',
              animationDelay: `${i * 200}ms`,
              willChange: 'transform, opacity',
            }} />
        ))}
      </div>

      {/* Initial dark veil → fades out for the 280ms reveal */}
      <div
        className="absolute inset-0 pointer-events-none transition-opacity duration-700"
        style={{
          background: '#0F0A04',
          opacity: revealed ? 0 : 1,
        }} />

      <style>{`
        /* Edge-to-edge: cover; on very tall phones switch to contain (blended edges) so the wordmark is never cropped */
        .lunora-splash-img { object-fit: cover; }
        @media (max-aspect-ratio: 3/5) {
          .lunora-splash-img {
            object-fit: contain;
            height: auto !important;
            margin: auto;
            -webkit-mask-image: linear-gradient(180deg, transparent 0, #000 10%, #000 90%, transparent 100%);
            mask-image: linear-gradient(180deg, transparent 0, #000 10%, #000 90%, transparent 100%);
          }
        }
        /* ─── Hero breathing — calm 6s scale loop ─────────────────────── */
        @keyframes lunora-hero-breathe {
          0%, 100% { transform: scale(1.000); }
          50%      { transform: scale(1.018); }
        }
        .lunora-hero-breathe { animation: lunora-hero-breathe 6.4s ease-in-out infinite; }

        /* ─── Bloom drift (two opposite directions) ──────────────────── */
        @keyframes lunora-bloom-1 {
          0%, 100% { transform: translate(0, 0); }
          50%      { transform: translate(15vw, 8vh); }
        }
        @keyframes lunora-bloom-2 {
          0%, 100% { transform: translate(0, 0); }
          50%      { transform: translate(-12vw, -10vh); }
        }
        .lunora-bloom-1 { animation: lunora-bloom-1 14s ease-in-out infinite; }
        .lunora-bloom-2 { animation: lunora-bloom-2 12s ease-in-out infinite; }

        /* ─── Shimmer particles drifting upward ───────────────────────── */
        @keyframes lunora-particle-rise {
          0%   { transform: translateY(0)      translateX(0)    scale(0.6); opacity: 0;    }
          12%  { opacity: 0.9; }
          88%  { opacity: 0.9; }
          100% { transform: translateY(-105vh) translateX(15px) scale(1.05); opacity: 0;   }
        }
        .lunora-particle {
          animation-name: lunora-particle-rise;
          animation-iteration-count: infinite;
          animation-timing-function: linear;
        }

        /* ─── Loading dots pulse ──────────────────────────────────────── */
        @keyframes lunora-dot {
          0%, 100% { transform: scale(0.7); opacity: 0.4; }
          50%      { transform: scale(1.1); opacity: 1;   }
        }
        .lunora-dot { animation: lunora-dot 1.2s ease-in-out infinite; }
      `}</style>
    </div>
  );
};

export default SplashScreen;
